import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo } from 'react';

import { DrawerResizeHandle } from '@/components/legacy-ui/DrawerResizeHandle';
import { useGetConversationTraces } from '@/hooks/useChat';
import { usePanelResize } from '@/hooks/usePanelResize';
import ConversationJobs from '@/sections/insights/aiAssistant/ConversationJobs';
import { useWorkflowStatus } from '@/sections/insights/aiAssistant/hooks/useWorkflowStatus';
import { useTraceDrawerStore } from '@/sections/insights/aiAssistant/stores/traceDrawer.store';
import { countLedgerJobs } from '@/sections/insights/aiAssistant/utils/jobLedger.util';
import { Conversation, TraceEvent } from '@/types';
import { cn } from '@/utils/classname';

import { SubagentRow } from './SubagentRow';
import { TraceHierarchy } from './TraceHierarchy';
import { WorkflowSpinner } from './WorkflowSpinner';

const MIN_WIDTH = 280;
const MAX_WIDTH = 600;
const STORAGE_KEY = 'ai-assistant-workflow-drawer-width';

type DrawerTab = 'jobs' | 'subagents' | 'workflow';

interface TraceDrawerProps {
  /** Trace ID of the currently-selected conversation (undefined if no workflow) */
  traceId?: string;
  /** In-progress background scans for the active conversation (dynamically shaped). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  jobs?: any[];
  isLoadingJobs?: boolean;
  hasActiveJobs?: boolean;
  childConversations?: Conversation[];
  currentConversationId?: string;
  parentConversationId?: string;
  onNavigateToConversation?: (conversationId: string) => void;
  /** Conversation ids with a pending credential request ("needs input"). */
  needsInputConversationIds?: Set<string>;
}

// Calculate workflow stats
function calculateWorkflowStats(traces: TraceEvent[]) {
  if (!traces || traces.length === 0) {
    return { totalDurationMs: 0, stepCount: 0 };
  }

  let earliest = Infinity;
  let latest = -Infinity;
  const spanIds = new Set<string>();

  traces.forEach(event => {
    if (!event) return;
    const cleanedTime = event.created?.includes('#')
      ? event.created.split('#').filter(Boolean).pop() || event.created
      : event.created;
    const normalizedTime = cleanedTime?.replace(/\.\d{4,}Z$/, match => {
      const ms = match.slice(1, 4);
      return `.${ms}Z`;
    });
    const time = new Date(normalizedTime || '').getTime();
    if (!isNaN(time)) {
      if (time < earliest) earliest = time;
      if (time > latest) latest = time;
    }
    if (event.span_id) {
      spanIds.add(event.span_id);
    }
  });

  if (earliest === Infinity || latest === -Infinity) {
    return { totalDurationMs: 0, stepCount: spanIds.size };
  }

  return {
    totalDurationMs: Math.max(0, latest - earliest),
    stepCount: spanIds.size,
  };
}

function formatTotalDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSec = seconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${remainingSec}s`;
  }
  return `${seconds}s`;
}

/**
 * Pick the default active tab for the current content (design-contract §1):
 * Jobs while anything is live, else Workflow if a trace exists, else the first
 * available tab (Jobs → Workflow → Subagents). Returns null when nothing has
 * content (drawer should close).
 */
function pickDefaultTab({
  hasActiveJobs,
  hasJobs,
  hasWorkflow,
  hasSubagents,
}: {
  hasActiveJobs: boolean;
  hasJobs: boolean;
  hasWorkflow: boolean;
  hasSubagents: boolean;
}): DrawerTab | null {
  if (hasActiveJobs && hasJobs) return 'jobs';
  if (hasWorkflow) return 'workflow';
  if (hasJobs) return 'jobs';
  if (hasSubagents) return 'subagents';
  return null;
}

/**
 * TraceDrawer - Side drawer displaying the agent's background work in three
 * tabs: Jobs (segmented scan ledger), Subagents (child conversations), and
 * Workflow (timeline trace). Slides in from the right; resizable by dragging
 * the left edge.
 */
export function TraceDrawer({
  traceId,
  jobs,
  isLoadingJobs,
  hasActiveJobs,
  childConversations = [],
  currentConversationId,
  parentConversationId,
  onNavigateToConversation,
  needsInputConversationIds,
}: TraceDrawerProps) {
  const isOpen = useTraceDrawerStore(s => s.isOpen);
  const activeTab = useTraceDrawerStore(s => s.activeTab);
  const close = useTraceDrawerStore(s => s.close);
  const setActiveTab = useTraceDrawerStore(s => s.setActiveTab);
  const pendingDefaultTab = useTraceDrawerStore(s => s.pendingDefaultTab);
  const clearPendingDefaultTab = useTraceDrawerStore(
    s => s.clearPendingDefaultTab
  );
  const { data: traces = [] } = useGetConversationTraces(traceId);
  const typedTraces = traces as TraceEvent[];
  const { isRunning, isComplete } = useWorkflowStatus(typedTraces);

  const jobCounts = countLedgerJobs(jobs);
  const liveJobs = !!hasActiveJobs;
  const hasJobs = jobCounts.total > 0;
  const hasSubagents = childConversations.length > 0;
  const hasWorkflow = !!traceId || liveJobs;
  const anyChildNeedsInput =
    !!needsInputConversationIds &&
    childConversations.some(c =>
      needsInputConversationIds.has(c.uuid || c.id || '')
    );

  // Reconcile drawer state on open and whenever content changes (e.g. a
  // conversation switch). Two cases (design-contract §1):
  //  1. `pendingDefaultTab` (a plain `open()` with no explicit tab, incl.
  //     auto-open) → pick the default tab: Jobs while live, else Workflow if a
  //     trace exists, else first available. Then clear the flag so a later tab
  //     click sticks.
  //  2. otherwise, only act when the active tab LOSES its content: fall through
  //     Jobs → Workflow → Subagents to the first with content; close the drawer
  //     when none have content.
  // An explicit `openTab(tab)` (e.g. HeaderBar "Subagents") clears the flag and
  // is preserved as long as that tab has content.
  useEffect(() => {
    if (!isOpen) return;

    const tabHasContent =
      (activeTab === 'jobs' && hasJobs) ||
      (activeTab === 'workflow' && hasWorkflow) ||
      (activeTab === 'subagents' && hasSubagents);

    if (!pendingDefaultTab && tabHasContent) return;

    const next = pickDefaultTab({
      hasActiveJobs: liveJobs,
      hasJobs,
      hasWorkflow,
      hasSubagents,
    });

    if (pendingDefaultTab) {
      clearPendingDefaultTab();
    }

    if (next === null) {
      close();
    } else if (next !== activeTab) {
      setActiveTab(next);
    }
  }, [
    isOpen,
    activeTab,
    pendingDefaultTab,
    hasJobs,
    hasWorkflow,
    hasSubagents,
    liveJobs,
    close,
    setActiveTab,
    clearPendingDefaultTab,
  ]);

  const { width: drawerWidth, onResizeHandleMouseDown } = usePanelResize({
    storageKey: STORAGE_KEY,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    defaultWidth: MAX_WIDTH, // open at max by default; a stored width still wins
  });

  const workflowStats = useMemo(
    () => calculateWorkflowStats(typedTraces),
    [typedTraces]
  );

  // Tab badges (design-contract §1).
  const jobsBadge = liveJobs ? (
    <span className="bg-chariot-brand-primary text-chariot-text-primary inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold">
      {jobCounts.active}
    </span>
  ) : (
    <span className="bg-chariot-background-secondary text-chariot-text-tertiary inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium">
      {jobCounts.total}
    </span>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: '100%', opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: '100%', opacity: 0 }}
          transition={{
            type: 'tween',
            duration: 0.25,
            ease: 'easeOut',
          }}
          className="bg-chariot-background-primary border-chariot-stroke absolute top-0 right-0 z-20 flex h-full flex-col border-l shadow-lg"
          style={{ width: `${drawerWidth}px` }}
        >
          {/* Resize handle */}
          <DrawerResizeHandle
            onMouseDown={onResizeHandleMouseDown}
            className="absolute top-1/2 -left-10 z-30 -translate-y-1/2"
          />
          {/* Header with tabs */}
          <div className="border-chariot-stroke border-b">
            <div className="flex items-center justify-between gap-2 px-4 pt-3 pb-0">
              {/* gap-3 (not gap-4) keeps all three labeled tabs + badges from
                  overflowing at the 280px MIN_WIDTH. (design-contract §1) */}
              <div className="flex min-w-0 gap-3">
                {hasJobs && (
                  <button
                    onClick={() => setActiveTab('jobs')}
                    className={cn(
                      'relative shrink-0 pb-2 text-sm font-medium whitespace-nowrap transition-colors',
                      activeTab === 'jobs'
                        ? 'text-chariot-text-primary'
                        : 'text-chariot-text-tertiary hover:text-chariot-text-secondary'
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      Jobs
                      {jobsBadge}
                    </span>
                    {activeTab === 'jobs' && (
                      <span className="bg-chariot-brand-primary absolute bottom-0 left-0 h-0.5 w-full rounded-full" />
                    )}
                  </button>
                )}
                {hasSubagents && (
                  <button
                    onClick={() => setActiveTab('subagents')}
                    className={cn(
                      'relative shrink-0 pb-2 text-sm font-medium whitespace-nowrap transition-colors',
                      activeTab === 'subagents'
                        ? 'text-chariot-text-primary'
                        : 'text-chariot-text-tertiary hover:text-chariot-text-secondary'
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      Subagents
                      <span className="bg-chariot-background-secondary text-chariot-text-tertiary rounded-full px-1.5 text-[10px]">
                        {childConversations.length}
                      </span>
                      {anyChildNeedsInput && (
                        <span
                          role="img"
                          aria-label="a subagent needs input"
                          className="relative flex size-2"
                        >
                          <span className="bg-chariot-status-warning absolute inline-flex size-full rounded-full opacity-75 motion-safe:animate-ping" />
                          <span className="bg-chariot-status-warning relative inline-flex size-2 rounded-full" />
                        </span>
                      )}
                    </span>
                    {activeTab === 'subagents' && (
                      <span className="bg-chariot-brand-primary absolute bottom-0 left-0 h-0.5 w-full rounded-full" />
                    )}
                  </button>
                )}
                {hasWorkflow && (
                  <button
                    onClick={() => setActiveTab('workflow')}
                    className={cn(
                      'relative shrink-0 pb-2 text-sm font-medium whitespace-nowrap transition-colors',
                      activeTab === 'workflow'
                        ? 'text-chariot-text-primary'
                        : 'text-chariot-text-tertiary hover:text-chariot-text-secondary'
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      Workflow
                      {isRunning && (
                        <span className="bg-chariot-brand-primary size-1.5 animate-pulse rounded-full" />
                      )}
                      {!isRunning && isComplete && (
                        <span className="text-success text-[10px]">✓</span>
                      )}
                    </span>
                    {activeTab === 'workflow' && (
                      <span className="bg-chariot-brand-primary absolute bottom-0 left-0 h-0.5 w-full rounded-full" />
                    )}
                  </button>
                )}
              </div>
              <button
                onClick={close}
                className="text-chariot-text-secondary hover:text-chariot-text-primary rounded p-1 transition-colors"
                aria-label="Close drawer"
              >
                <XMarkIcon className="size-5" />
              </button>
            </div>
            {/* Meta-line under the tabs (per active tab). */}
            {activeTab === 'jobs' && hasJobs && (
              <div className="text-chariot-text-tertiary px-4 pb-2 text-[10px]">
                {jobCounts.total} job{jobCounts.total !== 1 ? 's' : ''} total
                &middot; {jobCounts.active} active
              </div>
            )}
            {activeTab === 'workflow' && workflowStats.stepCount > 0 && (
              <div className="text-chariot-text-tertiary px-4 pb-2 text-[10px]">
                {workflowStats.stepCount} step
                {workflowStats.stepCount !== 1 ? 's' : ''} &middot;{' '}
                {formatTotalDuration(workflowStats.totalDurationMs)} total
              </div>
            )}
          </div>

          {/* Jobs tab content — persistent segmented ledger */}
          {activeTab === 'jobs' && (
            <div className="flex-1 overflow-y-auto p-4">
              <ConversationJobs jobs={jobs ?? []} isLoading={isLoadingJobs} />
            </div>
          )}

          {/* Workflow tab content — timeline-rail trace */}
          {activeTab === 'workflow' && (
            <>
              <div className="flex-1 overflow-y-auto p-4">
                {typedTraces.length > 0 ? (
                  <>
                    <div className="text-chariot-text-tertiary mt-1 mb-2.5 px-0.5 text-[10px] tracking-wide uppercase">
                      Workflow trace
                    </div>
                    <TraceHierarchy traces={typedTraces} animateEntries />
                  </>
                ) : (
                  <div className="text-chariot-text-secondary p-4 text-center text-sm">
                    Workflow actions taken by the assistant will be displayed
                    here
                  </div>
                )}
              </div>
              {isRunning && (
                <div className="border-chariot-stroke border-t">
                  <WorkflowSpinner size="sm" />
                </div>
              )}
            </>
          )}

          {/* Subagents tab content */}
          {activeTab === 'subagents' && (
            <div className="flex-1 overflow-y-auto">
              {parentConversationId && parentConversationId !== 'self' && (
                <button
                  onClick={() =>
                    onNavigateToConversation?.(parentConversationId)
                  }
                  className="text-chariot-brand-primary hover:bg-chariot-background-secondary border-chariot-stroke flex w-full items-center gap-1 border-b px-4 py-2 text-xs"
                >
                  <ChevronLeftIcon className="size-3" />
                  Back to parent
                </button>
              )}
              <div className="flex flex-col gap-2 p-3" role="list">
                {childConversations.map(child => {
                  const childId = child.uuid || child.id || '';
                  return (
                    <SubagentRow
                      key={childId}
                      conversation={child}
                      isViewing={childId === currentConversationId}
                      onNavigate={id => onNavigateToConversation?.(id)}
                      needsInput={needsInputConversationIds?.has(childId)}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default TraceDrawer;
